/**
 * Small numeric helpers shared by the domain layer.
 *
 * These are deliberately framework-free and total: `mean` and `stdev` of an
 * empty list return 0 rather than NaN, because every caller in the domain
 * layer would otherwise have to guard, and a NaN leaking into a rendered
 * figure is the failure mode this port exists to remove.
 */

export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const sum = (values: readonly number[]): number => values.reduce((a, b) => a + b, 0);

export const mean = (values: readonly number[]): number =>
  values.length === 0 ? 0 : sum(values) / values.length;

/** Population standard deviation. Returns 0 for an empty list. */
export const stdev = (values: readonly number[]): number => {
  if (values.length === 0) return 0;
  const m = mean(values);
  return Math.sqrt(mean(values.map((v) => (v - m) * (v - m))));
};

export const round = (value: number, decimals = 0): number => {
  const m = Math.pow(10, decimals);
  return Math.round(value * m) / m;
};

/**
 * Divide, treating a zero denominator as 1.
 *
 * Weight sums are the usual denominator in this codebase and can legitimately
 * reach zero when an operator disables every criterion. Returning the
 * numerator unchanged keeps the score finite and visibly wrong rather than
 * NaN, which is what the screens can actually communicate.
 */
export const safeDiv = (numerator: number, denominator: number): number =>
  numerator / (denominator || 1);
